export interface CityResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: string;
  type: string;
  importance: number;
  addresstype?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    hamlet?: string;
    municipality?: string;
    state?: string;
    country?: string;
    country_code?: string;
  };
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in both kilometers and miles
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): { km: number; miles: number } => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const km = R * c;
  const miles = km * 0.621371;

  return {
    km: Math.round(km * 10) / 10,
    miles: Math.round(miles * 10) / 10,
  };
};

/**
 * Estimate travel duration based on distance
 * Assumes average speed of 80 km/h for highway driving
 */
export const estimateDuration = (distanceKm: number): string => {
  const hours = distanceKm / 80;
  const totalMinutes = Math.round(hours * 60);

  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (m === 0) {
    return `${h}h`;
  }

  return `${h}h ${m}min`;
};

/**
 * Search cities using OpenStreetMap Nominatim API
 */
export const searchCities = async (query: string): Promise<CityResult[]> => {
  if (query.length < 2) return [];

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
        new URLSearchParams({
          q: query,
          format: "json",
          addressdetails: "1",
          limit: "8",
          featuretype: "city",
          "accept-language": "en",
        })
    );

    if (!response.ok) throw new Error("Search failed");

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any[] = await response.json();

    // Filter and format results to show cities/towns/villages
    return data
      .filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) =>
          item.type === "city" ||
          item.type === "town" ||
          item.type === "village" ||
          item.type === "administrative" ||
          item.class === "place" ||
          item.class === "boundary" ||
          (item.address &&
            (item.address.city || item.address.town || item.address.village))
      )
      .map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) => ({
          display_name: item.display_name,
          lat: item.lat,
          lon: item.lon,
          place_id: item.place_id,
          type: item.type,
          importance: item.importance || 0,
          addresstype: item.addresstype || item.type,
          address: item.address,
        })
      )
      .sort((a: CityResult, b: CityResult) => b.importance - a.importance);
  } catch (error) {
    console.error("City search error:", error);
    return [];
  }
};
